import { v3 } from "backoffice-api"
import { useQueryParam } from "hooks"
import {
  type Leaderboard as LeaderboardApi,
  leaderboard,
} from "leaderboard-api"
import { Loader } from "materia"
import { useTranslation } from "react-i18next"
import { QueryBoundary, WhenVisible } from "utility-components"
import { type Custom, custom } from "../../bonzai/bonzai"
import { getNextPageParamV1 } from "../../bonzai/getNextPageParamV1"
import { useFormatUser } from "../../bonzai/useFormatUser"
import { LeaderboardEntry } from "../../components/LeaderboardEntry"
import { getMaxScore } from "../../dataUtilities/getMaxScore"
import { getProductLink } from "../../dataUtilities/productDataUtilities"
import { usePickText } from "../../i18n/usePickText"
import { ConfigError } from "../../tools/ConfigError"
import { useIsFeatureFlagEnabled } from "../../tools/useIsFeatureFlagEnabled"
import { LeaderboardEmpty } from "./LeaderboardEmpty"
import { LeaderboardView } from "./LeaderboardView"

type LeaderboardScope = Custom["getLeaderboardScopes"][number]

export const LeaderboardViewLoader = () => (
  <LeaderboardView>
    <QueryBoundary fallback={<LeaderboardView.Skeleton />}>
      <Load />
    </QueryBoundary>
  </LeaderboardView>
)

const Load = () => {
  const { getIsPortalFlagEnabled } = useIsFeatureFlagEnabled()

  const isGlobalLeaderboardEnabled = !getIsPortalFlagEnabled(
    "disable_global_leaderboard"
  )

  const scopes = custom.getLeaderboardScopes.useSuspenseQuery([
    isGlobalLeaderboardEnabled,
  ])
  const firstScope = scopes[0]

  if (firstScope === undefined) {
    throw new ConfigError("No leaderboard scopes")
  }

  const [scope = firstScope.value, setScope] = useQueryParam("scope")

  return (
    <>
      <Scopes scopes={scopes} value={scope} onChange={setScope} />
      <QueryBoundary fallback={<Loader />}>
        <Leaderboards scope={scope} />
      </QueryBoundary>
    </>
  )
}

type ScopesProps = {
  value: string
  onChange: (value: string) => void
  scopes: LeaderboardScope[]
}
const Scopes = ({ scopes, value, onChange }: ScopesProps) => {
  const { t } = useTranslation()

  const scopeElements = scopes.map((scope) => (
    <LeaderboardView.Scope value={scope.value} key={scope.value}>
      {scope.name}
    </LeaderboardView.Scope>
  ))

  return (
    <LeaderboardView.Scopes
      value={value}
      onChange={onChange}
      header={t("leaderboard.ALL_LEADERBOARDS")}
    >
      {scopeElements}
    </LeaderboardView.Scopes>
  )
}

type LeaderboardsProps = {
  scope: string
}
const Leaderboards = ({ scope }: LeaderboardsProps) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useLeaderboardsData()

  const productIds = data.pages.flatMap((page) => page.data)
  const highScoresResults = useHighScores(productIds, scope)

  const isLoadingHighScores = highScoresResults.some(
    (result) => result.isPending
  )

  const leaderboards = productIds.flatMap((productId, index) => {
    const highScores = highScoresResults.at(index)?.data
    if (highScores === undefined || highScores.entries.length === 0) return []
    return [{ productId, highScores }]
  })

  const firstError = highScoresResults.find((result) => result.error)?.error

  // <WhenVisible> pages past all-empty pages, so nothing left here means
  // nothing exists. An error is different: we never learned whether anyone
  // played, so let QueryBoundary report it rather than claim "no players".
  if (leaderboards.length === 0 && !isLoadingHighScores && !hasNextPage) {
    if (firstError) throw firstError

    return <LeaderboardEmpty />
  }

  const leaderboardElements = leaderboards.map(({ productId, highScores }) => (
    <QueryBoundary key={productId} fallback={<LeaderboardView.Skeleton />}>
      <Leaderboard productId={productId} highScores={highScores} />
    </QueryBoundary>
  ))

  return (
    <LeaderboardView.Leaderboards>
      {leaderboardElements}
      <Loader isVisible={isLoadingHighScores || isFetchingNextPage} />

      <WhenVisible
        key={`${scope}-${productIds.length}`}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </LeaderboardView.Leaderboards>
  )
}

type LeaderboardProps = {
  productId: number
  highScores: LeaderboardApi["getHighScores"]
}
const Leaderboard = ({ productId, highScores }: LeaderboardProps) => {
  const { t } = useTranslation()
  const pickText = usePickText()
  const formatUser = useFormatUser()

  const product = v3.getProduct.useSuspenseQuery([productId], {
    select: (res) => res.data,
  })

  const maxScore = getMaxScore(highScores.entries)
  const showMyEntry = highScores.my_entry.value > 0

  const entries = highScores.entries.map((entry, index) => (
    <LeaderboardEntry
      // biome-ignore lint/suspicious/noArrayIndexKey: entries never reorder within a rendered board, and rank is not unique when players tie
      key={index}
      image={entry.user.image}
      imageDescription={entry.user.image_title["en-US"]}
      name={formatUser(entry.user)}
      rank={entry.rank}
      score={entry.value}
      maxScore={maxScore}
      isFirst={index === 0}
    />
  ))

  return (
    <LeaderboardView.Leaderboard
      link={{ to: getProductLink(product.id, product.product_type.identifier) }}
      title={pickText(product.title)}
      linkText={t("leaderboard.SEE_ALL")}
    >
      {entries}
      {showMyEntry && (
        <LeaderboardEntry
          image={highScores.my_entry.user.image}
          imageDescription={highScores.my_entry.user.image_title["en-US"]}
          isFirst={false}
          maxScore={maxScore}
          name={formatUser(highScores.my_entry.user)}
          rank={highScores.my_entry.rank}
          score={highScores.my_entry.value}
        />
      )}
    </LeaderboardView.Leaderboard>
  )
}

const useLeaderboardsData = () => {
  return custom.getPlayableProductIds.useSuspenseInfiniteQuery(
    [{ per_page: 6 }],
    {
      initialPageParam: { page: 1 },
      getNextPageParam: getNextPageParamV1,
    }
  )
}

// Deliberately does not throw per query: one failing product should cost one
// board, not the whole list. <Leaderboards> throws only if an error left it
// with nothing to render at all.
// TODO [ENGMAN-306] this fan-out goes away once leaderboards come from the BFF
// field, which pages server-side and reports emptiness in pageInfo.
const useHighScores = (productIds: number[], scope: string) => {
  return leaderboard.getHighScores.useQueries(
    productIds.map((product_id) => [{ product_id, scope, length: 3 }] as const)
  )
}
